import { Link } from "react-router-dom";
import { Alert } from "@monolith-forensics/monolith-ui";
import { useCaseFiles } from "@/Features/Files/Hooks/useCaseFiles";
import { isEvidencePhoto } from "@/Features/EvidencePhotos/evidence-photos.utils";
import { useEvidenceItemSummary } from "./evidence-files.query";
import { LearnMoreLink } from "./ManagedRowIcon";
import { StyledModalAlert } from "./StyledModalAlert";
import {
  evidenceFileNoun,
  evidenceItemFilesPath,
  evidenceItemLabel,
  isEvidenceFile,
} from "./evidence-files.utils";

type FileRow = {
  name: string;
  source_type?: string | null;
  source_id?: number | null;
  case?: { case_id: number } | null;
};

/**
 * The alert in the Case Files Delete and Edit modals on a file that lives in
 * an evidence item's folder (ENG-2133): a warning before a delete, a note on
 * an edit. The copy names the other places the file shows, which depends on
 * where the modal opened. Renders nothing for any other row.
 */
export const EvidenceFileModalAlert: React.FC<{
  file: FileRow;
  kind: "delete" | "edit";
}> = ({ file, kind }) => {
  const { surface, case_id } = useCaseFiles();
  const evidenceFile = isEvidenceFile(file);
  const { data: item } = useEvidenceItemSummary(file.source_id, {
    enabled: evidenceFile && surface === "case-files",
  });

  if (!evidenceFile) return null;

  const noun = evidenceFileNoun(file);
  const consequence =
    kind === "delete"
      ? "Deleting it here removes it there too."
      : "Changes here show there too.";

  let where: string;
  if (surface === "evidence-files-tab") {
    // Already on the item: name the other two places.
    where = isEvidencePhoto(file)
      ? "Case Files and this item's Photos tab"
      : "Case Files";
  } else {
    where = `the Files tab for ${evidenceItemLabel(item?.evidence_number)}`;
  }

  const showItemLink = kind === "edit" && surface === "case-files" && !!item;

  return (
    <StyledModalAlert data-testid={`evidence-file-${kind}-alert`}>
      <Alert variant={kind === "delete" ? "warning" : "info"}>
        <div className="modal-alert-body">
          This {noun} is also in {where}. {consequence}
          {showItemLink ? (
            <div className="modal-alert-links">
              <Link
                to={evidenceItemFilesPath(
                  item.uuid,
                  file.case?.case_id ?? case_id
                )}
              >
                View evidence item
              </Link>{" "}
              or <LearnMoreLink label="learn more" />
            </div>
          ) : (
            <>
              {" "}
              <LearnMoreLink />
            </>
          )}
        </div>
      </Alert>
    </StyledModalAlert>
  );
};
